import clsx from 'clsx'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Link } from 'src/components/Link/Link'
import { Modal } from 'src/components/Modal/Modal'
import { Price } from 'src/components/Price/Price'
import { Text } from 'src/components/Text/Text'
import { config } from 'src/config'
import { useCartQuery } from 'src/hooks/useCartQuery'

import styles from './PriceBox.module.scss'

interface PriceBoxProps {
  showShipping?: boolean
  showDiscounts?: boolean
  showVatInfo?: boolean
}

export const PriceBox = ({ showShipping = false, showDiscounts = false, showVatInfo = false }: PriceBoxProps) => {
  const {
    data: cart,
    computed: { hasShippingCosts, isFreeShipping, voucherDiscount },
  } = useCartQuery()
  const { t } = useTranslation()
  const [showShippingTermsModal, setShowShippingTermsModal] = useState(false)

  if (!cart) return null

  const {
    lines: { nodes: items },
    cost: { subtotalAmount },
    appliedGiftCards,
  } = cart
  const { currencyCode } = subtotalAmount

  const withSubscription = items.some((item) => !!item.sellingPlanAllocation)

  const shippingCostText = isFreeShipping ? (
    t('pdp.freeShipping')
  ) : (
    <Price price={{ amount: config.shippingFee, currencyCode }} />
  )

  const cartLinesWithDonation = items.find((item) => item.merchandise.product.id === config.donationProductId)
  const cartContainsDonationItem = Boolean(cartLinesWithDonation)
  const shippingCosts = hasShippingCosts && !isFreeShipping ? config.shippingFee : 0
  const giftCardAmount = appliedGiftCards.reduce((sum, card) => sum + parseFloat(card.presentmentAmountUsed.amount), 0)

  const adjustedTotalAmount = {
    amount: (parseFloat(subtotalAmount.amount) + shippingCosts - giftCardAmount).toFixed(2),
    currencyCode,
  }

  return (
    <div className={styles.rowsContainer}>
      <div className={styles.priceRow}>
        <div className={styles.column}>
          <Text small>{t('navbar.minicart.subtotalCartAmountLabel')}</Text>
        </div>

        <div className={styles.column}>
          <Text small>
            <Price price={subtotalAmount} />
          </Text>
        </div>
      </div>

      {/* voucher info */}
      {showDiscounts && voucherDiscount.amount < 0 && (
        <div className={styles.priceRow} data-testid="voucher-row">
          <div className={styles.column}>
            <Text small>
              <>{t('cart.discount')}</>
            </Text>
          </div>

          <div className={styles.column}>
            <Text small>{t('cart.savingsPreText')} </Text>
            <Text bold small className={styles.green}>
              <Price price={voucherDiscount} />
            </Text>
          </div>
        </div>
      )}

      {/* shipping info */}
      {hasShippingCosts && showShipping && (
        <>
          <div className={styles.priceRow}>
            <div className={styles.column}>
              <Text small>{t(withSubscription ? 'cart.singleShippingLabel' : 'common.shipping')}</Text>
            </div>

            <div className={styles.column}>
              <Text
                small
                className={(isFreeShipping && styles.green) || undefined}
                data-testid={isFreeShipping ? 'free-shipping-text' : 'shipping-costs'}
              >
                {shippingCostText}
              </Text>
            </div>
          </div>

          {withSubscription && (
            <div className={styles.priceRow}>
              <div className={styles.column}>
                <Text small>{t('cart.subscriptionShippingLabel')}</Text>
              </div>

              <div className={styles.column}>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link className={styles.smallText} onClick={() => setShowShippingTermsModal(true)}>
                  {t('cart.subscriptionShippingText')}
                </Link>
              </div>
            </div>
          )}
        </>
      )}

      {/* gift card info */}
      {showDiscounts &&
        appliedGiftCards.map(({ id, presentmentAmountUsed }) => (
          <div key={id} className={styles.priceRow} data-testid="giftcard-row">
            <div className={styles.column}>
              <Text small>
                <>{t('cart.giftCard')}</>
              </Text>
            </div>

            <div className={styles.column}>
              <Text bold small className={styles.green}>
                -<Price price={presentmentAmountUsed} />
              </Text>
            </div>
          </div>
        ))}

      {/* total price info */}
      <div className={clsx(styles.priceRow, styles.total)}>
        <div className={styles.column}>
          <Text bold>{t('navbar.minicart.totalCartAmountLabel')}</Text>
        </div>

        <div className={styles.column}>
          <Text bold>
            <Price price={adjustedTotalAmount} />
          </Text>
          {hasShippingCosts && showVatInfo && (
            <>
              <br />
              <Text className={styles.gray} small>
                {t('pdp.vatIncluded')}
                {cartContainsDonationItem && ` ${t('pdp.andDonation')}`}
              </Text>
            </>
          )}
        </div>
      </div>

      <Modal
        title={t('common.shippingTermsModal.title')}
        opened={showShippingTermsModal}
        onClose={() => setShowShippingTermsModal(false)}
      >
        <div dangerouslySetInnerHTML={{ __html: t('common.shippingTermsModal.body') }} suppressHydrationWarning />
      </Modal>
    </div>
  )
}
